// my.trumin — domain model. The spine is: Order → N seats (tickets) → each
// assigned to a Participant (self / managed child / invited adult).

import type { TicketEligibility } from './eligibility'

export type ID = string
/** '' = honest UNKNOWN: a comped/100%-off promo order can arrive with no
 *  total and priceless tickets, so no currency signal exists on the wire —
 *  consumers render the bare amount (BOARDROOM-2026-09-03-053306). */
export type Currency = 'AED' | 'USD' | 'GBP' | ''
export interface Money { amount: number; currency: Currency }

export type SeatStatus = 'unassigned' | 'pending' | 'assigned' | 'checked_in'
export type StepState = 'done' | 'now' | 'soon'

/**
 * One PLACE inside a ticket (canon §7: a Ticket holds N places; a Doubles
 * ticket = 2 seats — "2 of 2 to assign"). Ruling B (BOARDROOM-2026-08-09-133534):
 * the domain model carries a typed seats[] array even though today's my-api is
 * 1-ticket = 1-seat — the live single seatStatus degrades to a one-element
 * array (see singleSeat / api.ts mapping).
 *
 * TODO(infra seats[] contract, ruling B): the my-api seats-array change is
 * routed to infra separately. PROPOSED wire shape per ticket:
 *   tickets: [{ ticketId, typeName, seats: [{
 *     seatNo: number,            // 1-based, stable within the ticket
 *     holder: {                  // null ⇒ unassigned
 *       holderId: string,
 *       state: 'invited'|'claimed'|'ready'|'consumed'|'revoked',
 *       allocatedEmail: string | null,
 *       displayName: string | null,
 *       kind: 'self'|'child'|'invitee'|'anonymous',
 *     } | null,
 *   }] }]
 * Allocation endpoints gain a seatNo path segment
 * (/me/orders/:orderId/tickets/:ticketId/seats/:seatNo/allocation); the
 * existing seatless routes remain as seatNo=1 aliases for compatibility.
 */
export interface Seat {
  /** 1-based place number, stable within the ticket. Seat testids key on
   *  `{ticketId}.{seatNo}` (canon §1.1/§7.2). */
  seatNo: number
  status: SeatStatus
  /** This seat's OWN my-api ticket id — the allocate/revoke target for THIS
   *  place (BOARDROOM-2026-08-17-210222). A Team-of-N box-office entry is
   *  backed by N REAL tickets rows in my-api; applyAllocations stamps each
   *  seat with its row's id so assign/invite never re-targets seat 1's ticket
   *  (the prod 62970f6a 11-holders/10-revoked incident). Absent on fixture
   *  seats and on degraded seats my-api returned no row for — a degraded seat
   *  in an otherwise-resolved unit renders non-assignable (no fabrication). */
  ticketId?: string
  /** True when this seat is held by the signed-in account (the `You` pill /
   *  `mine` state, canon §7.2). Derived — never stored. */
  mine?: boolean
  /** Holder display identity when assigned/pending (name and/or email). */
  holder?: { name?: string; email?: string; kind?: Participant['kind'] }
}

/** Degrade mapping (ruling B): today's 1-ticket = 1-seat sources map their
 *  single seat status into a one-element seats[] array. */
export function singleSeat(status: SeatStatus, holder?: Seat['holder'], mine?: boolean): Seat[] {
  return [{ seatNo: 1, status, holder, mine }]
}

/**
 * Team ticket → N places (canon §7: "Doubles = 2 seats — 2 of 2 to assign").
 * A team ticket has `personsPerTicket` places; it is NOT single-assigned to one
 * person — one holder PER seat, and the ticket is complete only when every seat
 * is filled. Seat 1 carries the (single) box-office holder/status the wire
 * exposes today; seats 2..N are `unassigned` until the my-api seats[] wire
 * contract (TODO(infra seats[] contract), types.ts) exposes a per-seat holder
 * for each place. `persons < 1` / non-finite degrades to a single seat.
 */
export function seatsForPersons(persons: number, status: SeatStatus, holder?: Seat['holder'], mine?: boolean): Seat[] {
  const n = Number.isFinite(persons) && persons >= 1 ? Math.floor(persons) : 1
  const seats: Seat[] = [{ seatNo: 1, status, holder, mine }]
  for (let i = 2; i <= n; i++) seats.push({ seatNo: i, status: 'unassigned' })
  return seats
}

export interface Organiser {
  id: ID
  name: string        // "Apex Games"
  initials: string    // "AG"
  accentFg: string    // eyebrow/border colour
  accentBg: string    // soft tint
}

export interface TrEvent {
  id: ID
  name: string        // "Apex Trail Series"
  organiser: Organiser
  when: string        // display, e.g. "Sat 16 Aug · 09:00"
  venue?: string
  timingEnabled: boolean
  teamsEnabled: boolean
}

export interface Participant {
  id: ID
  // 'anonymous' = nameless/spectator holder per the LOCKED ticket-identity
  // model (boardroom task #18): auto-generated holder-ID, no PII, 0 required
  // steps. In the enum now for forward-compatibility — no anonymous flow is
  // built yet (OnePulse slice 1 only creates 'invitee').
  kind: 'self' | 'child' | 'invitee' | 'anonymous'
  name?: string
  initials?: string
  email?: string
  ageYears?: number
  hasLogin: boolean
}

/** A readiness step shown on the Get-ready card. */
export interface ReadyStep {
  key: 'profile' | 'waiver' | 'emergency' | 'questionnaire' | 'heat' | 'ticket'
  label: string
  meta: string
  state: StepState
  system?: boolean          // "we do this" — no participant action
  cta?: string              // Start / Sign / Add / View
  subject?: string          // child name for guardian variant
}

export interface Ticket {
  id: ID                    // "TICKET 04"
  orderId: ID
  eventId: ID
  typeName: string          // "Solo · PRO · Female"
  /** Box-office ticket_type_id (wire tickets[].ticketTypeId). Absent until
   *  tickets PR B ships it — the transfer entry hides without it. */
  ticketTypeId?: ID
  price: Money
  /** Legacy single-seat status (1-ticket = 1-seat my-api). Kept while the
   *  seats[] wire contract is routed to infra (ruling B); seats[] below is
   *  the canon-shaped source the §7 screens read. */
  seatStatus: SeatStatus
  /** Canon §7 places (ruling B) — today always singleSeat(seatStatus, …). */
  seats: Seat[]
  assignee?: Participant
  bib?: string
  wave?: { label: string; time: string }
  qrToken?: string
  invite?: { email: string; sentAt: string; expiresInDays: number }
  /** R1 eligibility contract for this ticket (gender + age band), when box-office
   *  exposes it on /consumer/me/orders (BOARDROOM-2026-08-11-201138). Absent ⇒
   *  unrestricted — the R3 eligibilityGate() treats it as allow. */
  eligibility?: TicketEligibility
  /** Team-purchase identity (BOARDROOM-2026-09-09-161918, Duncan customer-
   *  report): the my-api ticket_unit this entry belongs to — stamped by
   *  applyAllocations from the allocations wire so the Orders card can colour
   *  one team's blocks together and tell adjacent teams apart. ABSENT on solo
   *  entries, on the pre-ticketUnitId typeName fallback, and when my-api is
   *  unreadable (degrade = today's render, nothing fabricated). */
  teamUnitId?: string
  /** 1-based member position within the team purchase — set ONLY on the
   *  per-row wire (several same-type entries anchoring one unit), where each
   *  block IS one member ticket and wears the "… - Ticket {n}" suffix. A
   *  single entry that carries the whole unit (seats within one block) keeps
   *  its unsuffixed label — the places are already numbered inside it. */
  teamMemberSeq?: number
  /** Member count of the team purchase (per-row wire only, rides with seq). */
  teamMemberCount?: number
}

export interface Order {
  id: ID
  ref: string               // "ORD-U5RDG6"
  eventId: ID
  purchasedAt: string       // "18 Jul 2026"
  total: Money
  invoiceUrl?: string
  ticketIds: ID[]
}

export interface Profile {
  fullName: string
  email: string
  dob: string
  gender: string
  nationality?: string
  phone: string             // E.164
  emergency?: { name: string; phone: string }
  medical?: { consented: boolean; notes?: string }
  managedChildren: Participant[]
}
